<template>
  <v-container fluid class="eviz-container">
    <v-stepper alt-labels :items="stepsName" hide-actions :model-value="stepId">
      <template v-slot:[`item.1`]>
        <v-card flat>
          <div class="viz-design-grid">
            <div
              v-for="type in visualizationTypes"
              :key="type.value"
              class="viz-design-card"
              :class="{
                'viz-design-card--active': selectedVisualization === type.value,
                'viz-design-card--disabled': type.disabled
              }"
            >
              <div class="viz-design-title">{{ type.title }}</div>
              <div class="viz-design-image-frame">
                <img :src="type.image" :alt="type.title" class="viz-design-image">
              </div>
              <v-btn
                class="viz-design-select"
                :class="{ 'viz-design-select--active': selectedVisualization === type.value }"
                color="primary"
                block
                :disabled="type.disabled"
                @click="selectVisualization(type.value)"
              >
                {{ type.disabled ? 'COMING SOON' : 'SELECT' }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </template>

      <template v-slot:[`item.2`]>
        <v-card flat>
          <div class="d-flex align-center mb-4">
            <v-icon class="mr-2">mdi mdi-help-circle</v-icon>
            <v-btn
              variant="text"
              color="primary"
              @click="showFormatDialog = true"
              class="pa-0"
              style="text-transform: none; font-size: 0.875rem; min-width: auto; letter-spacing: normal;"
            >
              See data format
            </v-btn>
          </div>

          <div class="upload-fields">
            <v-file-input
              v-model="nodeFile"
              clearable
              label="Upload node file"
              variant="solo-filled"
              show-size
              accept=".csv,.xlsx,.xls"
              prepend-icon="mdi-file-upload"
              class="text-subtitle-1"
              :disabled="rendering"
              @update:model-value="handleNodeFileChange"
            ></v-file-input>
            <v-file-input
              v-model="edgeFile"
              clearable
              label="Upload edge file"
              variant="solo-filled"
              show-size
              accept=".csv,.xlsx,.xls"
              prepend-icon="mdi-file-upload"
              class="text-subtitle-1"
              :disabled="rendering"
              @update:model-value="handleEdgeFileChange"
            ></v-file-input>
          </div>

          <div v-if="selectedFiles.length > 0 && !useExampleData" class="file-info-section mt-3">
            <v-card variant="outlined" class="pa-3">
              <div class="text-subtitle-2 font-weight-medium mb-3">
                Selected Files ({{ selectedFiles.length }})
              </div>

              <div
                v-for="(fileInfo, index) in selectedFiles"
                :key="fileInfo.name"
                class="file-item"
                :class="{ 'mb-3': index < selectedFiles.length - 1 }"
              >
                <div class="d-flex align-center">
                  <v-avatar size="40" class="mr-3">
                    <v-icon size="24" :color="resolveRoleColor(fileInfo.role)">
                      mdi-file-delimited
                    </v-icon>
                  </v-avatar>

                  <div class="flex-grow-1">
                    <div class="text-subtitle-2 font-weight-medium">{{ fileInfo.name }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ formatFileSize(fileInfo.size) }} • CSV file • {{ fileInfo.sourceLabel }}
                    </div>
                  </div>

                  <div class="ml-3 d-flex flex-wrap gap-2 justify-end">
                    <v-chip size="small" :color="resolveRoleColor(fileInfo.role)" variant="tonal">
                      {{ fileInfo.roleLabel }}
                    </v-chip>
                    <v-chip
                      size="small"
                      :color="fileInfo.source === 'example' ? 'info' : 'secondary'"
                      variant="tonal"
                    >
                      {{ fileInfo.source === 'example' ? 'Example' : 'Upload' }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </v-card>
          </div>

          <div class="d-md-flex align-center justify-space-between mb-2 mt-4">
            <v-checkbox
              v-model="useExampleData"
              label="Try example data"
              hide-details
              :disabled="rendering"
              @update:model-value="handleExampleDataChange"
            ></v-checkbox>
            <div class="d-flex flex-wrap mt-2 mt-md-0 justify-end">
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="mdi-download"
                style="margin-right: 10px;"
                @click="downloadExampleFile('nodesCsv')"
              >
                Nodes CSV
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="mdi-download"
                style="margin-right: 10px;"
                @click="downloadExampleFile('edgesCsv')"
              >
                Edges CSV
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="mdi-download"
                style="margin-right: 10px;"
                @click="downloadExampleFile('nodesXlsx')"
              >
                Nodes XLSX
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                prepend-icon="mdi-download"
                @click="downloadExampleFile('edgesXlsx')"
              >
                Edges XLSX
              </v-btn>
            </div>
          </div>

          <v-alert
            v-if="validationError"
            class="mt-4"
            type="error"
            variant="tonal"
            density="compact"
          >
            <v-icon start>mdi-alert</v-icon>
            {{ validationError }}
          </v-alert>

          <v-alert
            v-if="validationSummary && !useExampleData"
            class="mt-4"
            type="success"
            variant="tonal"
            density="compact"
          >
            <v-icon start>mdi-check-circle</v-icon>
            {{ validationSummary }}
          </v-alert>
        </v-card>
      </template>

      <template v-slot:[`item.3`]>
        <div class="summary-shell">
          <v-row class="summary-row">
            <v-col cols="12">
              <v-card flat>
                <v-card-text class="summary-card-text pa-0">
                  <v-card variant="outlined">
                    <v-card-title class="d-flex align-center">
                      <v-icon class="mr-2" color="primary">mdi-graph-outline</v-icon>
                      <span class="text-body-2 font-weight-medium">Result Preview</span>
                    </v-card-title>
                    <v-card-text class="pa-0">
                      <ebio-chart
                        v-if="chartReady"
                        :key="chartRenderKey"
                        :local-node-csv="chartNodeCsv"
                        :local-edge-csv="chartEdgeCsv"
                        :chart-title="chartTitle"
                        :skip-user-status="true"
                        :category-color-overrides="categoryColorOverrides"
                      />
                    </v-card-text>
                  </v-card>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </template>

      <div v-if="stepId !== 1" class="stepper-actions">
        <v-divider class="mx-4 mb-4"></v-divider>
        <div class="step-content">
          <v-spacer></v-spacer>
          <v-btn
            :disabled="stepId === 1"
            class="mr-4"
            size="large"
            variant="tonal"
            color="primary"
            @click="stepperPre"
          >
            Prev
          </v-btn>
          <v-btn
            :disabled="!canProceedToNext || rendering || stepId === stepsName.length"
            size="large"
            variant="tonal"
            color="primary"
            @click="stepperNext"
          >
            <span v-if="rendering">Loading...</span>
            <span v-else>Next</span>
          </v-btn>
        </div>
      </div>
    </v-stepper>

    <v-dialog v-model="showFormatDialog" max-width="760" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          Data Format Requirements
          <v-spacer></v-spacer>
          <v-btn @click="showFormatDialog = false" size="small" variant="text" class="square-close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            EVIZ requires two CSV files in one render session: one nodes file and one edges file.
          </p>
          <div class="mb-3">
            <div class="font-weight-medium mb-1">Nodes CSV required columns</div>
            <div>`EXID`, `Preferred name`, `Group name`</div>
          </div>
          <div class="mb-3">
            <div class="font-weight-medium mb-1">Edges CSV required columns</div>
            <div>`Source`, `Target`, `Source preferred name`, `Target preferred name`, `Source group`, `Target group`</div>
          </div>
          <p class="mb-0">
            Filenames should preferably contain <code>nodes</code> or <code>edges</code>.
            The page also validates file content before allowing the summary step.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { mapActions, mapGetters } from 'vuex';
import EbioChart from '@/views/ebio/ebioChart.vue';
import correlationHeatmapImage from '@/assets/viz/correlation_heatmap.png';
import networkImage from '@/assets/viz/newwork.jpg';
import volcanoPlotImage from '@/assets/viz/volcano_plot.png';

const REQUIRED_NODE_COLUMNS = ['EXID', 'Preferred name', 'Group name'];
const REQUIRED_EDGE_COLUMNS = ['Source', 'Target'];

export default {
  name: 'EvizView',
  components: {
    EbioChart
  },
  data() {
    return {
      stepsName: ['Select type', 'Upload data', 'Plot'],
      stepId: 1,
      selectedVisualization: '',
      visualizationTypes: [
        {
          value: 'network',
          title: 'Network',
          image: networkImage,
          disabled: false
        },
        {
          value: 'volcano',
          title: 'Volcano Plot',
          image: volcanoPlotImage,
          disabled: true
        },
        {
          value: 'correlation',
          title: 'Correlation heatmap',
          image: correlationHeatmapImage,
          disabled: true
        }
      ],
      nodeFile: null,
      edgeFile: null,
      selectedFiles: [],
      useExampleData: false,
      exampleFilesCache: [],
      validationError: '',
      validationSummary: '',
      rendering: false,
      showFormatDialog: false,
      chartReady: false,
      chartNodeCsv: '',
      chartEdgeCsv: '',
      chartRenderKey: 0,
      chartTitle: 'EVIZ Network',
      categoryColorOverrides: {
        Exposure: '#5470c6',
        Disease: '#ee6666',
        GO: '#fc8452',
        Protein: '#91cc75',
        Protein_1: '#91cc75',
        'Protein_1 (prot)': '#91cc75',
        'Protein_1 (met)': '#91cc75',
        Protein_2: '#fac858',
        'Protein_2 (prot)': '#fac858',
        'Protein_2 (met)': '#fac858',
        Protein_3: '#73c0de',
        'Protein_3 (prot)': '#73c0de',
        'Protein_3 (met)': '#73c0de',
        Protein_4: '#9a60b4',
        'Protein_4 (prot)': '#9a60b4',
        'Protein_4 (met)': '#9a60b4'
      }
    };
  },
  async mounted() {
    // 进入模块时主动上报一次 eviz 使用（非阻断：未登录/失败不影响本地渲染）
    await this.createNewSession();
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    canProceedToNext() {
      if (this.stepId === 1) {
        return Boolean(this.selectedVisualization);
      }

      if (this.stepId === 2) {
        return this.useExampleData || (Boolean(this.nodeFile) && Boolean(this.edgeFile));
      }
      return false;
    }
  },
  methods: {
    ...mapActions(['createSession']),

    // 进入模块时上报一次 eviz 使用（非阻断：未登录或失败均静默跳过，不影响本地渲染）
    async createNewSession() {
      try {
        if (!this.isLoggedIn) {
          return;
        }
        await this.createSession({ module_name: 'eviz' });
      } catch (error) {
        console.warn('创建 eviz 会话失败，不影响继续操作:', error);
      }
    },

    selectVisualization(value) {
      const visualization = this.visualizationTypes.find(type => type.value === value);
      if (!visualization || visualization.disabled) return;
      this.selectedVisualization = value;
      this.stepId = 2;
    },

    normalizeSingleFile(file) {
      if (Array.isArray(file)) return file[0] || null;
      return file || null;
    },

    handleNodeFileChange(file) {
      this.nodeFile = this.normalizeSingleFile(file);
      this.onUploadFilesChange();
    },

    handleEdgeFileChange(file) {
      this.edgeFile = this.normalizeSingleFile(file);
      this.onUploadFilesChange();
    },

    onUploadFilesChange() {
      if ((this.nodeFile || this.edgeFile) && this.useExampleData) {
        this.useExampleData = false;
        this.exampleFilesCache = [];
      }

      this.resetRenderState();
      this.rebuildUploadSelection();
    },

    rebuildUploadSelection() {
      const entries = [];
      if (this.nodeFile) {
        entries.push(this.buildSelectedFileEntry({
          name: this.nodeFile.name,
          size: this.nodeFile.size,
          role: 'nodes',
          source: 'upload'
        }));
      }
      if (this.edgeFile) {
        entries.push(this.buildSelectedFileEntry({
          name: this.edgeFile.name,
          size: this.edgeFile.size,
          role: 'edges',
          source: 'upload'
        }));
      }
      this.selectedFiles = entries;
    },

    async handleExampleDataChange(value) {
      this.useExampleData = Boolean(value);
      this.resetRenderState();

      if (!this.useExampleData) {
        this.exampleFilesCache = [];
        this.selectedFiles = [];
        return;
      }

      this.nodeFile = null;
      this.edgeFile = null;
      this.selectedFiles = this.createExampleSelectionPlaceholder();
      this.rendering = true;

      try {
        const parsedFiles = await this.loadExampleFiles();
        this.exampleFilesCache = parsedFiles;
        this.selectedFiles = parsedFiles.map(item => this.buildSelectedFileEntry({
          name: item.file.name,
          size: item.file.size,
          role: item.role,
          source: 'example'
        }));
      } catch (error) {
        this.useExampleData = false;
        this.exampleFilesCache = [];
        this.selectedFiles = [];
        this.validationError = error.message || 'Failed to load example data.';
      } finally {
        this.rendering = false;
      }
    },

    async stepperNext() {
      if (this.stepId === 1) {
        this.stepId = 2;
        return;
      }

      if (this.stepId !== 2) return;
      const rendered = await this.renderNetwork();
      if (rendered) {
        this.stepId = 3;
      }
    },

    stepperPre() {
      if (this.stepId > 1) {
        this.stepId -= 1;
      }
    },

    async renderNetwork() {
      this.rendering = true;
      this.resetRenderState();

      try {
        let parsedFiles = [];

        if (this.useExampleData) {
          parsedFiles = this.exampleFilesCache.length > 0
            ? this.exampleFilesCache
            : await this.loadExampleFiles();
        } else {
          if (!this.nodeFile || !this.edgeFile) {
            throw new Error('Please upload one node file and one edge file.');
          }

          const files = [this.nodeFile, this.edgeFile];
          parsedFiles = await Promise.all(files.map(async file => {
            const text = await this.readFileAsCsvText(file);
            return this.parseAndValidateFile(file.name, text, file.size);
          }));
        }

        if (this.useExampleData) {
          this.exampleFilesCache = parsedFiles;
        }

        const nodesFiles = parsedFiles.filter(item => item.role === 'nodes');
        const edgesFiles = parsedFiles.filter(item => item.role === 'edges');
        this.validateParsedFiles(nodesFiles, edgesFiles);

        this.chartNodeCsv = nodesFiles[0].text;
        this.chartEdgeCsv = edgesFiles[0].text;
        this.chartRenderKey += 1;
        this.chartReady = true;
        this.selectedFiles = parsedFiles.map(item => ({
          ...this.buildSelectedFileEntry({
            name: item.file.name,
            size: item.file.size,
            role: item.role,
            source: this.useExampleData ? 'example' : 'upload'
          })
        }));
        this.validationSummary = this.useExampleData
          ? `Validated bundled example files ${nodesFiles[0].file.name} as nodes and ${edgesFiles[0].file.name} as edges.`
          : `Validated ${nodesFiles[0].file.name} as nodes and ${edgesFiles[0].file.name} as edges.`;
        return true;
      } catch (error) {
        this.chartReady = false;
        this.chartNodeCsv = '';
        this.chartEdgeCsv = '';
        this.validationError = error.message || 'Failed to validate the uploaded files.';
        return false;
      } finally {
        this.rendering = false;
      }
    },

    async loadExampleFiles() {
      const examplePaths = [
        '/exampleData/demo_nodes.csv',
        '/exampleData/demo_edges.csv'
      ];

      const results = await Promise.all(examplePaths.map(async path => {
        const response = await fetch(path);
        if (!response.ok) {
          throw new Error(`Failed to load example file: ${path}.`);
        }

        const text = await response.text();
        const fileName = path.split('/').pop() || path;
        return this.parseAndValidateFile(fileName, text, this.estimateTextSize(text));
      }));

      const nodesFiles = results.filter(item => item.role === 'nodes');
      const edgesFiles = results.filter(item => item.role === 'edges');
      this.validateParsedFiles(nodesFiles, edgesFiles);

      return results;
    },

    parseAndValidateFile(fileName, text, size) {
      const csv = Papa.parse(text, {
        header: true,
        skipEmptyLines: true
      });
      const headers = csv.meta.fields || [];
      const role = this.resolveFileRole(fileName, headers);

      return {
        file: {
          name: fileName,
          size
        },
        text,
        headers,
        role
      };
    },

    resolveFileRole(fileName, headers) {
      const nameRole = this.detectRoleFromName(fileName);
      const schemaRole = this.detectRoleFromHeaders(headers);

      if (nameRole && schemaRole && nameRole !== schemaRole) {
        throw new Error(`File "${fileName}" looks like ${nameRole} by filename but matches ${schemaRole} by content. Please fix the filename or file content.`);
      }

      return nameRole || schemaRole || 'unknown';
    },

    detectRoleFromName(fileName) {
      const lowerName = String(fileName || '').toLowerCase();
      if (lowerName.includes('nodes')) return 'nodes';
      if (lowerName.includes('edges')) return 'edges';
      return '';
    },

    detectRoleFromHeaders(headers) {
      const headerSet = new Set(headers);
      const isNodeFile = REQUIRED_NODE_COLUMNS.every(column => headerSet.has(column));
      const isEdgeFile = REQUIRED_EDGE_COLUMNS.every(column => headerSet.has(column));

      if (isNodeFile && !isEdgeFile) return 'nodes';
      if (isEdgeFile && !isNodeFile) return 'edges';
      return '';
    },

    assertRequiredColumns(headers, requiredColumns, role) {
      const missingColumns = requiredColumns.filter(column => !headers.includes(column));
      if (missingColumns.length > 0) {
        throw new Error(`The ${role} CSV is missing required columns: ${missingColumns.join(', ')}.`);
      }
    },

    validateParsedFiles(nodesFiles, edgesFiles) {
      if (nodesFiles.length !== 1 || edgesFiles.length !== 1) {
        if (nodesFiles.length === 0) {
          throw new Error('A valid nodes CSV was not found. Make sure one file is the nodes file and includes EXID, Preferred name, and Group name.');
        }
        if (edgesFiles.length === 0) {
          throw new Error('A valid edges CSV was not found. Make sure one file is the edges file and includes Source and Target.');
        }
        throw new Error('Duplicate file roles detected. Please upload exactly one nodes CSV and one edges CSV.');
      }

      this.assertRequiredColumns(nodesFiles[0].headers, REQUIRED_NODE_COLUMNS, 'nodes');
      this.assertRequiredColumns(edgesFiles[0].headers, REQUIRED_EDGE_COLUMNS, 'edges');
    },

    readFileText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => resolve(String(event.target?.result || ''));
        reader.onerror = () => reject(new Error(`Failed to read file "${file.name}".`));
        reader.readAsText(file);
      });
    },

    // 读取上传文件并统一转成 CSV 文本：xlsx/xls 用 SheetJS 转换，其余按纯文本读取
    async readFileAsCsvText(file) {
      const lowerName = String(file.name || '').toLowerCase();
      if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
        return this.readXlsxAsCsvText(file);
      }
      return this.readFileText(file);
    },

    readXlsxAsCsvText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => {
          try {
            const workbook = XLSX.read(new Uint8Array(event.target.result), { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            if (!firstSheetName) {
              throw new Error(`No worksheet found in "${file.name}".`);
            }
            resolve(XLSX.utils.sheet_to_csv(workbook.Sheets[firstSheetName]));
          } catch (error) {
            reject(new Error(error.message || `Failed to parse "${file.name}".`));
          }
        };
        reader.onerror = () => reject(new Error(`Failed to read file "${file.name}".`));
        reader.readAsArrayBuffer(file);
      });
    },

    downloadExampleFile(type) {
      const fileMap = {
        nodesCsv: { source: 'demo_nodes.csv', downloadName: 'Nodes CSV.csv' },
        edgesCsv: { source: 'demo_edges.csv', downloadName: 'Edges CSV.csv' },
        nodesXlsx: { source: 'demo_nodes.xlsx', downloadName: 'Nodes XLSX.xlsx' },
        edgesXlsx: { source: 'demo_edges.xlsx', downloadName: 'Edges XLSX.xlsx' }
      };
      const fileInfo = fileMap[type];
      if (!fileInfo) return;
      const link = document.createElement('a');
      link.href = `/exampleData/${fileInfo.source}`;
      link.download = fileInfo.downloadName;
      link.click();
    },

    roleLabel(role) {
      if (role === 'nodes') return 'Nodes';
      if (role === 'edges') return 'Edges';
      return 'Unresolved';
    },

    buildSelectedFileEntry({ name, size, role, source }) {
      return {
        name,
        size,
        role,
        roleLabel: this.roleLabel(role),
        source,
        sourceLabel: source === 'example' ? 'Bundled example data' : 'Uploaded by user'
      };
    },

    createExampleSelectionPlaceholder() {
      return [
        this.buildSelectedFileEntry({
          name: 'demo_nodes.csv',
          size: 0,
          role: 'nodes',
          source: 'example'
        }),
        this.buildSelectedFileEntry({
          name: 'demo_edges.csv',
          size: 0,
          role: 'edges',
          source: 'example'
        })
      ];
    },

    resetRenderState() {
      this.validationError = '';
      this.validationSummary = '';
      this.chartReady = false;
      this.chartNodeCsv = '';
      this.chartEdgeCsv = '';
    },

    resolveRoleColor(role) {
      if (role === 'nodes') return 'primary';
      if (role === 'edges') return 'success';
      return 'warning';
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`;
    },

    estimateTextSize(text) {
      return new TextEncoder().encode(String(text || '')).length;
    }
  }
};
</script>

<style scoped>
.eviz-container {
  padding-left: 12px;
  padding-right: 12px;
}

.upload-fields {
  width: 100%;
}

@media (max-width: 600px) {
  .upload-fields {
    width: 100%;
  }
}

.viz-design-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
}

.viz-design-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 220px;
  padding: 16px 20px 20px;
  border: 1px solid #eeeeee;
  background: #ffffff;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.viz-design-card:nth-child(2) {
  margin-left: 20px;
  margin-right: 20px;
}

.viz-design-card--active {
  background: #ffffff;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px #1976d2;
}

.viz-design-card--disabled {
  cursor: default;
}

.viz-design-title {
  min-height: 24px;
  margin-bottom: 12px;
  color: #222222;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
}

.viz-design-image-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 250px;
  margin-bottom: 16px;
  overflow: hidden;
}

.viz-design-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.viz-design-select {
  height: 36px !important;
  min-height: 36px !important;
  border-radius: 3px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
}

.viz-design-select--active {
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.55), 0 1px 4px rgba(25, 118, 210, 0.28) !important;
}

.file-info-section .v-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.file-item {
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.file-item:not(:last-child) {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
  margin-bottom: 12px;
}

.stepper-actions {
  margin-top: 20px;
}

.step-content {
  padding: 16px;
  display: flex;
  gap: 8px;
}

.square-close-btn {
  border-radius: 4px !important;
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
}

.summary-shell {
  margin-left: 0;
  margin-right: 0;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card-text {
  padding-left: 12px !important;
  padding-right: 12px !important;
}

@media (min-width: 960px) {
  .eviz-container {
    padding-left: 20px;
    padding-right: 20px;
  }

  .summary-card-text {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}

@media (max-width: 600px) {
  .viz-design-grid {
    grid-template-columns: 1fr;
    gap: 26px;
  }

  .viz-design-title {
    font-size: 21px;
  }

  .viz-design-image-frame {
    margin-bottom: 14px;
  }

  :deep(.v-stepper-item:not(.v-stepper-item--selected) .v-stepper-item__title) {
    display: none !important;
  }

  :deep(.v-stepper-item:not(.v-stepper-item--selected)) {
    opacity: 0.6;
    transform: scale(0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 50px;
  }

  :deep(.v-stepper-item:not(.v-stepper-item--selected) .v-stepper-item__avatar) {
    width: 28px !important;
    height: 28px !important;
    font-size: 12px !important;
  }

  :deep(.v-stepper-item--selected) {
    min-width: 110px;
    opacity: 1;
    transform: scale(1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  :deep(.v-stepper-item--selected .v-stepper-item__avatar) {
    width: 40px !important;
    height: 40px !important;
    font-size: 16px !important;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
  }

  :deep(.v-stepper-item--selected .v-stepper-item__title) {
    display: block !important;
    font-size: 14px !important;
    font-weight: 600;
    color: #1976d2;
    white-space: normal;
    line-height: 1.2;
  }

  :deep(.v-stepper-header) {
    padding: 12px 8px;
    justify-content: space-between;
  }

  :deep(.v-stepper-item__divider) {
    margin: 0 4px;
    flex: 0 0 auto;
    min-width: 20px;
  }

  .step-content {
    flex-direction: row;
    justify-content: space-between !important;
    gap: 12px;
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .step-content .v-btn {
    flex: 1;
    min-width: 0;
    height: 48px !important;
    font-size: 16px !important;
    font-weight: 600;
    margin: 0 !important;
  }

  .step-content .v-btn:disabled {
    opacity: 0.5;
  }

  :deep(.v-stepper) {
    padding-bottom: 80px;
  }
}
</style>
